import { uniq, uniqBy } from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiFactory from "../api";
import { SOCKET_STATUS, chatEvents$, socketStatus$ } from "../api/webSocketRx";
import { MAIL, MESSAGE_STATUS, conversationType } from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import { openNotificationError, openNotificationSuccess } from "../utils/Utils";
import { useMenuContext } from "./MenuContext";

const MailContext = createContext(null);
export const useMailContext = () => {
  return useContext(MailContext);
};

export const MailProvider = ({ children }) => {
  const {
    selectedConversation,
    setSelectedConversation,
    conversationList,
    handleUpdateConversationList,
    messageSearch,
  } = useMenuContext();

  const { user } = useInfoUser();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchTopMessage, setIsFetchTopMessage] = useState(false);
  const [paging, setPaging] = useState({
    fetchCount: 0,
    totalItems: 0,
  });

  const [bottomMessageList, setBottomMessageList] = useState([]);
  const [topMessageList, setTopMessageList] = useState([]);

  const [totalNotificationItem, setTotalNotificationItem] = useState(0);
  const [isReply, setIsReply] = useState(false);
  const [userList, setUserList] = useState([]);

  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [cCOrgList, setCCOrgList] = useState([]);
  const [memberOrgList, setMemberOrgList] = useState([]);
  const [isModalLabelGroup, setIsModalLabelGroup] = useState(false);
  const [isForwardAllMail, setIsForwardAllMail] = useState(false);
  const [isModalMembersReadUnread, setIsModalMembersReadUnread] =
    useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const [title, setTitle] = useState("");
  const [isOpenModalDeleteConversation, setIsOpenModalDeleteConversation] =
    useState(false);

  const updateUserList = async (userIds) => {
    if (!selectedConversation?.conversationId) return;

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: userIds,
    });

    if (userResult?.status !== 200) {
      return openNotificationError(userResult?.message);
    }

    if (userResult?.data?.length) {
      setUserList((prevUserList) => {
        const combinedUsers = [...prevUserList, ...userResult.data];
        return uniqBy(combinedUsers, "userId");
      });
    }
  };

  const getListUserFromMail = async (userIds) => {
    if (!selectedConversation?.conversationId) return [];

    if (!userIds || userIds?.length === 0) return [];

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: uniq(userIds),
    });

    if (userResult?.status !== 200) {
      openNotificationError(userResult?.message);
      return [];
    }

    return userResult?.data || [];
  };

  const fetchBottomMessages = async (
    signal,
    totalItems = 0,
    topMessagesLength = 0,
  ) => {
    if (!selectedConversation?.conversationId) return;
    setIsLoading(true);
    const limit = Math.min(MAIL.BOTTOM_MESSAGE, totalItems - topMessagesLength);

    if (limit === 0) return;

    try {
      const response = await apiFactory.letterApi.getPage(
        {
          limit,
          page: 1,
          search: {
            conversationId: selectedConversation?.conversationId,
            searchContent: null,
          },
          direction: "ASC",
        },
        signal,
      );

      if (response?.status !== 200) {
        openNotificationError(response?.message);
        return;
      }

      const items = response?.data?.items || [];
      const sortedMessages = items?.sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
      );

      setBottomMessageList(sortedMessages);

      return response;
    } catch (error) {
      if (error?.name !== "AbortError") {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopMessagesV2 = async (signal) => {
    if (!selectedConversation?.conversationId) return;

    setIsLoading(true);

    try {
      const response = await apiFactory.letterApi.getPage(
        {
          limit: MAIL.TOP_MESSAGE,
          search: {
            conversationId: selectedConversation?.conversationId,
            searchContent: null,
          },
          direction: "DESC",
        },
        signal,
      );

      if (response?.status !== 200) {
        openNotificationError(response?.message);
        return;
      }

      const topMessages = response?.data?.items || [];

      setTotalNotificationItem(response?.data?.totalItems || 0);
      setTopMessageList(topMessages);
      setIsFetchTopMessage(true);

      let userIds = topMessages.map((item) => item?.senderId).filter(Boolean);

      const bottomResponse = await fetchBottomMessages(
        signal,
        response?.data?.totalItems || 0,
        topMessages?.length || 0,
      );

      const bottomMessages = bottomResponse?.data?.items || [];
      setPaging({
        fetchCount: topMessages?.length + bottomMessages?.length,
        totalItems: response?.data?.totalItems || 0,
      });

      userIds = [
        ...userIds,
        ...bottomMessages.map((item) => item?.senderId).filter(Boolean),
      ];

      const users = await getListUserFromMail(userIds);
      setUserList([...(users || [])]);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addMailList = (letter) => {
    setTopMessageList((prev) => {
      /** mailId in draftMail is original parent mail */
      const draftMailIndex = prev?.findIndex(
        (m) => m?.letterId === letter?.draftMail?.letterId,
      );

      if (draftMailIndex >= 0) {
        prev[draftMailIndex].draftMail = null;
      }

      return [letter, ...prev].sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
      );
    });

    setBottomMessageList((prev) => {
      const draftMailIndex = prev?.findIndex(
        (m) => m?.letterId === letter?.draftMail?.letterId,
      );

      if (draftMailIndex >= 0) {
        prev[draftMailIndex].draftMail = null;
      }

      return [...prev];
    });
  };

  const updateNotificationItem = (eventData) => {
    if (!selectedConversation?.conversationId) return;
    if (selectedConversation?.type !== conversationType.NOTIFICATION) return;
    if (!eventData?.letter) return;

    const { announcer, seenMessageTs, type } = eventData ?? {};

    const letter = eventData?.letter;

    // SEEN_MESSAGE
    if (type === MESSAGE_STATUS.SEEN_MESSAGE && seenMessageTs) {
      const { conversationId, lastOldSeenTs } = seenMessageTs;
      if (conversationId !== selectedConversation?.conversationId) return;

      const lastOld = new Date(lastOldSeenTs);
      const seenUpdater = (list) =>
        (list || []).map((m) => {
          const unread = Math.max(m?.unreadCount || 0, 0);
          const created = new Date(m?.createdAt);
          const updated = lastOld > created ? unread : Math.max(unread - 1, 0);
          return { ...m, unreadCount: updated };
        });

      setBottomMessageList(seenUpdater);
      setTopMessageList(seenUpdater);
      return;
    }

    const mailConversationId = letter?.conversationId;
    if (
      !mailConversationId ||
      mailConversationId !== selectedConversation?.conversationId
    )
      return;

    addMailList(letter);
  };

  // =========================
  // Actions
  // =========================
  const onDeleteMessageNotifications = async () => {
    try {
      const rs = await apiFactory.mailApi.deleteMessageNotifications({
        conversationId: selectedConversation?.conversationId,
      });

      if (rs?.status !== 200) {
        openNotificationError("Delete messages notification failed");
      } else {
        openNotificationSuccess("Delete messages notification successfully");

        handleUpdateConversationList(
          (conversationList || []).filter(
            (item) =>
              item?.conversationId !== selectedConversation?.conversationId,
          ),
        );
      }
    } catch (error) {
      openNotificationError("Something went wrong!");
    } finally {
      setSelectedConversation(null);
      navigate("/conversation");
    }
  };

  const onLoadMore = async () => {
    if (!selectedConversation?.conversationId) return;

    const res = await apiFactory.letterApi.getPage({
      limit: Math.min(MAIL.TOP_MESSAGE, paging.totalItems - paging.fetchCount),
      maxTs: topMessageList?.[topMessageList?.length - 1]?.createdAt,
      minTs: bottomMessageList?.[0]?.createdAt,
      search: {
        conversationId: selectedConversation?.conversationId,
        searchContent: null,
      },
      direction: "DESC",
    });

    if (res?.status !== 200) {
      openNotificationError(res?.message);
      return;
    }

    const items = res?.data?.items || [];

    const userIds = items?.map((item) => item?.senderId).filter(Boolean);

    const rsUsers = await getListUserFromMail(userIds);
    if (rsUsers?.length) {
      setUserList((prevUserList) => {
        const combinedUsers = [...prevUserList, ...rsUsers];
        return uniqBy(combinedUsers, "userId");
      });
    }

    const sortedMessages = [...items, ...(topMessageList || [])]?.sort(
      (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
    );

    setTopMessageList(sortedMessages);
    setIsMoreMessage(items?.length === MAIL.MORE_MESSAGE);
    setTotalNotificationItem(res?.data?.totalItems || 0);
  };

  useEffect(() => {
    setTopMessageList([]);
    setBottomMessageList([]);
    setTotalNotificationItem(0);
    setIsFetchTopMessage(false);

    const controller = new AbortController();
    fetchTopMessagesV2(controller.signal);

    return () => controller.abort();
  }, [selectedConversation?.conversationId, messageSearch]);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type !== conversationType.NOTIFICATION) return;

      const letter = eventData?.letter || eventData?.message;
      const mailConversationId =
        letter?.conversationId || eventData?.seenMessageTs?.conversationId;

      if (
        mailConversationId &&
        mailConversationId !== selectedConversation?.conversationId
      ) {
        return;
      }

      updateNotificationItem(eventData);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const prevStatusRef = { current: null };

    const sub = socketStatus$.subscribe((s) => {
      if (!s || s.scope !== "chat") return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type !== conversationType.NOTIFICATION) return;

      const prev = prevStatusRef.current;
      prevStatusRef.current = s.status;

      // vừa reconnect xong -> refetch
      if (
        prev !== SOCKET_STATUS.CONNECTED &&
        s.status === SOCKET_STATUS.CONNECTED
      ) {
        const controller = new AbortController();
        // fetchTopMessages(controller.signal);
        // không cần cleanup ở đây vì request nhỏ; nếu bạn muốn strict:
        // return () => controller.abort();
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  const values = useMemo(
    () => ({
      userList,
      setUserList,
      getListUserFromMail,
      topMessageList,
      setTotalNotificationItem,
      totalNotificationItem,
      selectedConversation,
      setIsFetchTopMessage,
      bottomMessageList,
      setBottomMessageList,
      setIsReply,
      isReply,
      isMoreMessage,
      setIsMoreMessage,
      title,
      setTitle,
      cCOrgList,
      setCCOrgList,
      memberOrgList,
      setMemberOrgList,
      isFetchTopMessage,
      setTopMessageList,
      isModalLabelGroup,
      setIsModalLabelGroup,
      onDeleteMessageNotifications,
      onLoadMore,
      isLoading,
      setIsLoading,
      setIsOpenModalDeleteConversation,
      isOpenModalDeleteConversation,
      updateUserList,
      isForwardAllMail,
      setIsForwardAllMail,
      isModalMembersReadUnread,
      setIsModalMembersReadUnread,
      selectedMessage,
      setSelectedMessage,
    }),
    [
      totalNotificationItem,
      isFetchTopMessage,
      userList,
      topMessageList,
      selectedConversation,
      bottomMessageList,
      isReply,
      isMoreMessage,
      title,
      cCOrgList,
      memberOrgList,
      isModalLabelGroup,
      isLoading,
      isOpenModalDeleteConversation,
      isForwardAllMail,
      isModalMembersReadUnread,
      selectedMessage,
    ],
  );

  return <MailContext.Provider value={values}>{children}</MailContext.Provider>;
};
